import React from 'react';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import { Grid } from '@mui/material';
import { SnackContext } from 'src/store/ContextStore';
import { getColor } from 'src/util/util';
import { renderTagChips } from './tagChips';
import AnonymousFreeTextBanner from '../../AnonymousFreeTextBanner';
import { memberIdentityOf } from '../memberIdentity';
import { withPersonTags } from '../personTagRows';
import axios from 'src/axios';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  useChangedFields
} from 'src/components/UI/input/changed-field';

// Person-level tags: labels on an individual member (the selected node), stored
// on the working models (UserChanged for the proband, FamilyChanged for other
// members). Mirrors the family-level Tags.jsx and shares the org palette
// (Client.clientTags) fetched via GET /pedigree/tags.

const isSystemTag = (tag) => {
  const lower = (tag?.label ?? tag)?.toLowerCase() ?? '';
  return lower.startsWith('imported from') || lower.includes('merge');
};

// The labels the editor shows for a member's stored tags. System tags are
// neither offered nor posted, so they are left out here too.
export const editableTagLabels = (personTags) =>
  (Array.isArray(personTags) ? personTags : [])
    .map((t) => t?.tag)
    .filter((t) => t && !isSystemTag(t));

// THE SECTION MUST SHOW THE SELECTED PERSON'S TAGS.
//
// Every sidebar section stays mounted across a change of person, so this
// component is not remounted when someone else is selected. It used to read
// the tags once, on mount, and never again: person B's Tags section showed
// person A's, and a Save from it wrote A's tags, plus the edit, onto B. Nothing
// had to be dirty for that to happen.
//
// So the editor follows the member's row in memberRecords, matched on
// memberIdentityOf (`_id`, never the pid), and re-seeds whenever the person or
// their stored tags change. The row rather than `data`: `data` is a click-time
// snapshot that nothing refreshes when only the tags change.
//
// AND A SAVE HAS TO REACH THAT ROW. The tags are written by their own endpoint,
// POST /pedigree/person-tags, which never touched memberRecords. That left two
// things reading the pre-save tags: this editor on the way back to the person,
// and PedigreeChart's autosave, whose POST /pedigree-config $sets every
// non-proband member's WHOLE row, personTags included. The next unrelated edit
// in the family therefore wrote the old tags back over the saved ones. The row
// is now updated before the request, without arming a pedigree save, and
// rolled back if the save is refused: see personTagRows.js, which every writer
// to that endpoint shares.
//
// Unsaved tag edits are left alone while the same person's row refreshes, and
// they are what marks the section dirty, so the sidebar's selection guard asks
// before a change of person discards them. Dirty means the tags DIFFER from the
// person's tags as last saved (TGCLOUD-1902): a tag removed and added back is no
// change, and a Save is no longer a change only once the server has taken it.
function PersonTags({
  data,
  memberRecords,
  setMemberRecords,
  user,
  onDirtyChange,
  // How many fields hold an unsaved change: 1 or 0 (TGCLOUD-1902).
  onChangeCount,
  saveRef,
  discardRef
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);

  const identity = memberIdentityOf(data);
  const row = React.useMemo(
    () =>
      (identity != null &&
        (memberRecords || []).find((m) => memberIdentityOf(m) === identity)) ||
      data,
    [memberRecords, identity, data]
  );
  const storedTags = row?.personTags;
  const loadedTags = React.useMemo(
    () => editableTagLabels(storedTags),
    [storedTags]
  );
  const loadedKey = loadedTags.join('\n');

  const familyMemberId = data?.familyMemberId ?? data?.proBandId;
  const isProband = !!data?.proBandId;
  const user_id = user?.user_id || user?._id;

  const [tags, setTags] = React.useState(loadedTags);
  // A Save on its way: the row already holds its tags (written before the
  // request), but they are not saved until the server says so (TGCLOUD-1902,
  // rule 7), so the stored row is not the baseline meanwhile.
  const pendingRef = React.useRef(null);
  // Changed = the tags differ from the person's tags as last saved, by value: a
  // tag removed and added back is no change. Another person re-reads the
  // baseline in the same render (resetKey).
  const changes = useChangedFields({
    baseline: { tags: loadedTags },
    values: { tags },
    fields: ['tags'],
    resetKey: identity
  });
  const { resetBaseline } = changes;

  // Re-seed DURING RENDER, not in an effect. An effect would commit one render
  // holding the new person's identity with the previous person's tags, and the
  // save handler is refreshed from each committed render: a Save landing in
  // that window would post A's tags against B.
  const [synced, setSynced] = React.useState({ identity, loadedKey });
  if (synced.identity !== identity || synced.loadedKey !== loadedKey) {
    const samePerson = synced.identity === identity;
    setSynced({ identity, loadedKey });
    if (!samePerson) {
      pendingRef.current = null;
      setTags(loadedTags);
    } else if (!pendingRef.current) {
      // The same person's row refreshed from elsewhere: that is what is saved
      // now. An unsaved edit is kept, and compared with it.
      resetBaseline({ tags: loadedTags });
      if (!changes.isChanged) setTags(loadedTags);
    }
  }

  // Read by a save that resolves after the clinician has moved on.
  const latestRef = React.useRef(null);
  latestRef.current = { identity, tags };

  React.useEffect(() => {
    onDirtyChange?.(changes.isChanged);
  }, [changes.isChanged]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    onChangeCount?.(changes.count);
  }, [changes.count]); // eslint-disable-line react-hooks/exhaustive-deps

  const [options, setOptions] = React.useState(
    CONSTANTS.tags.filter((t) => !isSystemTag(t))
  );

  React.useEffect(() => {
    axios.get(APICONSTANTS.setTags).then(({ data: { clientTags } }) => {
      if (clientTags.length > 0) {
        setOptions(clientTags.filter((t) => !isSystemTag(t)));
      }
    });
  }, []);

  const handleTags = (value) => {
    const next = [];
    value.map((tag) => {
      if (typeof tag === 'string' && !next.includes(tag)) next.push(tag);
      else {
        const label = tag?.inputValue || tag?.label;
        label && !next.includes(label) && next.push(label);
      }
    });
    setTags(next);
  };

  const onSave = async () => {
    const savedIdentity = identity;
    const savedLabels = tags;
    const previousTags = storedTags;
    const clientTags = [];
    const personTags = [];
    tags.forEach((tag) => {
      const tagColor =
        options.find((t) => t.label === tag)?.tagColor ?? getColor(tag);
      clientTags.push({ label: tag, tagColor });
      personTags.push({ tag, tagColor });
    });

    pendingRef.current = savedLabels;
    setMemberRecords?.((prev) =>
      withPersonTags(prev, savedIdentity, personTags)
    );

    await axios
      .post(APICONSTANTS.personTags, {
        user_id,
        familyMemberId,
        isProband,
        clientTags,
        personTags
      })
      .then(() => {
        // Saved: these tags are the baseline now, for the person they were
        // saved for. An edit made since stays a change against them.
        pendingRef.current = null;
        if (latestRef.current.identity === savedIdentity) {
          resetBaseline({ tags: savedLabels });
        }
        setSnack({ status: true, msg: t('Tags Updated'), severity: 'success' });
      })
      .catch(() => {
        pendingRef.current = null;
        setMemberRecords?.((prev) =>
          withPersonTags(prev, savedIdentity, previousTags, personTags)
        );
        setSnack({
          status: true,
          msg: t('An error occured'),
          severity: 'error'
        });
      });
  };

  const onDiscard = () => {
    setTags(changes.baseline.tags ?? loadedTags);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <>
      <AnonymousFreeTextBanner sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        <ChangedField
          component={Grid}
          size={12}
          name="tags"
          changed={changes.changed.tags}
        >
          <AutocompleteInput
            multiple
            options={options}
            getOptionLabel={(option) => option}
            value={tags}
            onChange={(v) => handleTags(v)}
            renderValue={renderTagChips(options)}
          />
        </ChangedField>
      </Grid>
    </>
  );
}

export default PersonTags;
